/**
 * EVENT CONSTANTS
 * ===============
 * askk-specific values for the analytics envelope. The *mechanism* (mirrored
 * from ShareChat's vibely-web) is settled; these VALUES are not.
 *
 * ⚠️ ALL VALUES BELOW ARE PLACEHOLDERS — confirm with the data/analytics team
 * before relying on the events (ticket VB-2607). Until EVENTS_ENDPOINT is set,
 * analytics runs in "dry" mode: it logs the payload to the console instead of
 * sending it (see api/events.ts), so dev/testing never fires bad data.
 */

/**
 * Events ingestion endpoint (shared ShareChat BQ events API).
 *
 * Hit DIRECTLY (no dev proxy). It's a DIFFERENT origin from askk-web, so the POST
 * is cross-origin — the events server must CORS allow-list the web origin (and
 * the dev origin for local testing). `.env.production` sets the real URL; dev
 * sets the same in `.env.local`.
 *
 * Empty ('') = dry mode (log only, don't send). Defaults to dry so an
 * unconfigured build never fires live events — builds must explicitly set
 * REACT_APP_EVENTS_ENDPOINT to the real URL.
 */
export const EVENTS_ENDPOINT: string = process.env.REACT_APP_EVENTS_ENDPOINT ?? ''

// Values confirmed against a live askk-android events POST (tenant=askk).
// askk-android sends client:"android"/clientType:"Android"; for web we report
// 'web' on both.
export const CLIENT_NAME = 'web'
export const CLIENT_TYPE = 'web'
export const APP_NAME = 'askk'
// `appV`/`appVersion` is NOT a constant — it comes from the native app that
// opened the WebView (see getAppVersion() in lib/cookies.ts).
export const BROKER_NAME = 'broker1' // `bn` in the envelope
export const TENANT = 'askk' // `x-tenant`

/**
 * Numeric ShareChat event id (`i`). The warehouse identifies an event by this
 * number, so the envelope must carry one. We use a SINGLE id for the whole
 * horoscope feature — the readable section/action/component fields below do the
 * real differentiating.
 */
export const HOROSCOPE_EVENT_ID = 730767090

/**
 * Event taxonomy — the client-decided descriptors that identify each event.
 * Kept as const maps so feature code references names, not magic strings.
 */

/**
 * `section` — the feed section an event belongs to. For the page open/close it's
 * the page itself; for everything inside the feed it's the widget's API `type`
 * (BANNER_IMAGE / TODAYS_PREDICTION / ASTROLOGER_CARD), so widgets pass their
 * own type string and we don't duplicate the list here.
 */
export const EventSection = {
  HOROSCOPE_PAGE: 'HOROSCOPE_PAGE',
  /** The full-screen astro form shown before the horoscope (birth-details gate). */
  ASTRO_FORM: 'ASTRO_FORM',
} as const

/** `action` — what the user did. */
export const EventAction = {
  OPEN: 'open',
  CLOSE: 'close',
  VIEW: 'view',
  CLICK: 'click',
} as const

/** `component` — the specific part of a section that was acted on. */
export const EventComponent = {
  LUCKY_NUMBER: 'lucky_number',
  LUCKY_COLOR: 'lucky_color',
  LUCKY_TIME: 'lucky_time',
  /** The astrologer card as a whole (a pseudo-click, NOT the CTAs). */
  CARD: 'card',
  // The call/chat CTAs are reported as `cta_audio` / `cta_chat` (the suffix is the
  // action's mediaType), built at the call site in AstrologerCard.

  /* ---- Astro form (section = ASTRO_FORM) ---- */
  // NOTE (CodeRabbit flag, intentionally NOT removed): like every value in this
  // file these are placeholders pending data-team sign-off (see header, VB-2607).
  // They emit nothing in prod until REACT_APP_EVENTS_ENDPOINT is set, so they're
  // safe to keep; deleting them would only break the form's logEvent call sites.
  // Finalizing the values is a launch step, not a pre-merge change.
  /** Opened the place-of-birth search overlay. */
  PLACE_SEARCH: 'place_search',
  /** Picked a city in the place-of-birth overlay. */
  PLACE_SELECTED: 'place_selected',
  /** Tapped the submit CTA with a valid form. */
  SUBMIT: 'submit',
  /** Tapped submit but validation blocked it (meta carries the failing fields). */
  VALIDATION_ERROR: 'validation_error',
} as const

export type EventActionName = (typeof EventAction)[keyof typeof EventAction]
